// Calendar.jsx
import React, { useState, useEffect, useRef } from "react";
import "../styles/calendar.css";
import "../styles/calendar_extended.css";
import moment from "moment";

const Calendar = ({
  initialDate = new Date(),
  events = [],
  onDateClick,
  onEventClick,
}) => {
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [view, setView] = useState("month");
  const [transformedEvents, setTransformedEvents] = useState({});
  const [activeEvent, setActiveEvent] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);

  const popupRef = useRef(null);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const weekDaysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  //start change
  const handleMoreEventsClick = (eventsOfDay, e) => {
  e.stopPropagation();

  setSelectedDayEvents(eventsOfDay);
  setShowAllEvents(true);
  setActiveEvent(null);
};

const handleDialogEventClick = (event) => {
  setShowAllEvents(false);

  // Put the normal event popup in the center of the screen
  const popupWidth = 250;
  const popupHeight = 280;

  const x = Math.max(
    10,
    (window.innerWidth - popupWidth) / 2
  );

  const y = Math.max(
    10,
    (window.innerHeight - popupHeight) / 2
  );

  setPopupPosition({ x, y });
  setActiveEvent(event);
};
  //end chsnges*******************

  // Transform events to calendar-friendly format (adds id)
  useEffect(() => {
    const eventsByDate = {};

    if (Array.isArray(events)) {
      events.forEach((event, idx) => {
        const eventDate = moment(event.date);
        const dateKey = eventDate.format("YYYY-M-D");

        if (!eventsByDate[dateKey]) eventsByDate[dateKey] = [];

        // ensure stable id
        const id =
          event.id ?? event.caseId ?? `${event.title}-${dateKey}-${idx}`;

        const calendarEvent = {
          id,
          title: event.title,
          color: getEventColor(event),
          time: event.time ? event.time.split(" - ")[0] : "",
          caseNumber: event.caseId ? `Case #${event.caseId}` : "",
          description: event.room || "",
          originalEvent: event,
        };

        eventsByDate[dateKey].push(calendarEvent);
      });
    }

    setTransformedEvents(eventsByDate);
  }, [events]);

  // Color rules
  const getEventColor = (event) => {
    if (event.room?.includes("Court 1")) return "blue";
    if (event.room?.includes("Court 2")) return "red";
    if (event.room?.includes("Court 3")) return "green";
    if (event.room?.includes("Court 4")) return "purple";
    if (event.room?.includes("Room")) return "orange";
    return "blue";
  };

  // date helpers
  const getDaysInMonth = (date) =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

  const getFirstDayOfMonth = (date) =>
    new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const generateCalendarDays = () => {
    const days = [];
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);

    // previous month padding
    const prevMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      0
    );
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        day: daysInPrevMonth - i,
        isCurrentMonth: false,
        isPrevMonth: true,
      });
    }

    // current month
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ day: d, isCurrentMonth: true });
    }

    // next month padding (to fill 6 weeks)
    const remainingCells = 42 - days.length;
    for (let i = 1; i <= remainingCells; i++) {
      days.push({ day: i, isCurrentMonth: false, isPrevMonth: false });
    }

    return days;
  };

  const getWeekDates = () => {
    const startOfWeek = new Date(currentDate);
    const dayOfWeek = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDates.push(date);
    }
    return weekDates;
  };

  const goToPreviousPeriod = () => {
    if (view === "month") {
      setCurrentDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
      );
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 7);
      setCurrentDate(newDate);
    }
  };

  const goToNextPeriod = () => {
    if (view === "month") {
      setCurrentDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
      );
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 7);
      setCurrentDate(newDate);
    }
  };

  const goToToday = () => setCurrentDate(new Date());

  const isToday = (
    day,
    month = currentDate.getMonth(),
    year = currentDate.getFullYear()
  ) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      month === today.getMonth() &&
      year === today.getFullYear()
    );
  };

  const getEventsForDay = (
    day,
    month = currentDate.getMonth(),
    year = currentDate.getFullYear()
  ) => {
    const dateKey = `${year}-${month + 1}-${day}`;
    return transformedEvents[dateKey] || [];
  };

  const getEventsForDateTime = (date, hour) => {
    const dateKey = `${date.getFullYear()}-${
      date.getMonth() + 1
    }-${date.getDate()}`;
    const dayEvents = transformedEvents[dateKey] || [];
    return dayEvents.filter((event) => {
      if (!event.time) return false;
      const timeMoment = moment(event.time, ["h:mm A", "HH:mm"]);
      return timeMoment.hour() === hour;
    });
  };

  const handleDayClick = (dayObj) => {
    if (!onDateClick) return;
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    if (dayObj.isPrevMonth) onDateClick(new Date(year, month - 1, dayObj.day));
    else if (dayObj.isCurrentMonth)
      onDateClick(new Date(year, month, dayObj.day));
    else onDateClick(new Date(year, month + 1, dayObj.day));
  };

  // EVENT CLICK — sets popup position and activeEvent
  const handleEventClick = (calendarEvent, e) => {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const padding = 10;

    // Calculate position - try to place it near the event
    let x = rect.left;
    let y = rect.bottom + padding;

    // Check if popup would go off-screen
    const popupWidth = 250;
    const popupHeight = 280;

    // Adjust horizontal position if needed
    if (x + popupWidth > window.innerWidth) {
      x = window.innerWidth - popupWidth - padding;
    }

    // Adjust vertical position if needed
    if (y + popupHeight > window.innerHeight) {
      y = rect.top - popupHeight - padding;
    }

    setPopupPosition({ x, y });
    setActiveEvent(calendarEvent);
  };

  // Render up to 3 events inside a day
  const renderEvents = (eventsOfDay) => {
  // 3 or fewer: show everything normally
  if (eventsOfDay.length <= 3) {
    return eventsOfDay.map((event, i) => (
      <div
        key={event.id ?? i}
        className={`event ${event.color}`}
        onClick={(e) => handleEventClick(event, e)}
        role="button"
        tabIndex={0}
      >
        {event.title}
      </div>
    ));
  }

  // More than 3:
  // Show first 2 events + "more" as the third item
  return (
    <>
      {eventsOfDay.slice(0, 2).map((event, i) => (
        <div
          key={event.id ?? i}
          className={`event ${event.color}`}
          onClick={(e) => handleEventClick(event, e)}
          role="button"
          tabIndex={0}
        >
          {event.title}
        </div>
      ))}

      <div
        className="event more-events"
        onClick={(e) => handleMoreEventsClick(eventsOfDay, e)}
        role="button"
        tabIndex={0}
      >
        +{eventsOfDay.length - 2} more
      </div>
    </>
    );
};

  const timeSlots = [
    "8:00 AM",
    "9:00 AM",
    "10:00 AM",
    "11:00 AM",
    "12:00 PM",
    "1:00 PM",
    "2:00 PM",
    "3:00 PM",
    "4:00 PM",
    "5:00 PM",
    "6:00 PM",
  ];

  const formatWeekRange = () => {
    const weekDates = getWeekDates();
    const start = weekDates[0];
    const end = weekDates[6];

    if (start.getMonth() === end.getMonth()) {
      return `${
        monthNames[start.getMonth()]
      } ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
    }
    return `${monthNames[start.getMonth()]} ${start.getDate()} - ${
      monthNames[end.getMonth()]
    } ${end.getDate()}, ${end.getFullYear()}`;
  };

  // close popup on outside click or Escape
  useEffect(() => {
    const handleOutside = (e) => {
      if (popupRef.current && popupRef.current.contains(e.target)) return;
      setActiveEvent(null);
    };

    const handleEsc = (e) => {
      if (e.key === "Escape") setActiveEvent(null);
    };

    window.addEventListener("mousedown", handleOutside);
    window.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const renderMonthView = () => {
    const calendarDays = generateCalendarDays();

    return (
      <div className="calendar month-view">
        <div className="days-header">
          {daysOfWeek.map((day) => (
            <div key={day} className="day-name">
              {day}
            </div>
          ))}
        </div>

        <div className="days-grid">
          {calendarDays.map((dayObj, index) => {
            const monthOffset = dayObj.isPrevMonth
              ? currentDate.getMonth() - 1
              : dayObj.isCurrentMonth
              ? currentDate.getMonth()
              : currentDate.getMonth() + 1;

            const dayEvents = getEventsForDay(dayObj.day, monthOffset);
            const todayCheck = isToday(dayObj.day, monthOffset);

            return (
              <div
                key={index}
                className={`day-cell ${
                  !dayObj.isCurrentMonth ? "other-month" : ""
                }`}
                onClick={() => handleDayClick(dayObj)}
              >
                <div className={`day-number ${todayCheck ? "today" : ""}`}>
                  {dayObj.day}
                </div>

                {dayEvents.length > 0 && (
                    <div className="events-container">
                        {renderEvents(dayEvents)}
                    </div>
                    )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDates = getWeekDates();

    return (
      <div className="calendar week-view">
        <div className="week-header">
          <div className="time-column-header">Time</div>
          {weekDates.map((date, index) => (
            <div key={index} className="week-day-header">
              <div className="week-day-name">
                {weekDaysOfWeek[date.getDay()]}
              </div>
              <div className="week-day-date">
                {monthNames[date.getMonth()].slice(0, 3)} {date.getDate()}
              </div>
            </div>
          ))}
        </div>

        <div className="week-grid">
          {timeSlots.map((time, timeIndex) => (
            <div key={timeIndex} className="time-row">
              <div className="time-label">{time}</div>

              {weekDates.map((date, dateIndex) => {
                const hour = timeIndex + 8;
                const timeEvents = getEventsForDateTime(date, hour);

                if (hour === 12)
                  return (
                    <div key={dateIndex} className="time-cell lunch-break">
                      <div className="lunch-label">Lunch Break</div>
                    </div>
                  );

                return (
                  <div key={dateIndex} className="time-cell">
                    {timeEvents.map((event, eventIndex) => (
                      <div
                        key={event.id ?? eventIndex}
                        className={`week-event ${event.color}`}
                        onClick={(e) => handleEventClick(event, e)}
                      >
                        <div className="week-event-title">
                          {event.caseNumber || event.title}
                        </div>
                        <div className="week-event-description">
                          {event.description || event.title}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };
const AllEventsDialog = ({ events, onClose, onEventClick }) => {
  return (
    <div
      className="all-events-overlay"
      onClick={onClose}
    >
      <div
        className="all-events-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="all-events-header">
          <div>
            <h3>Hearings</h3>
            <span>
              {events.length} scheduled hearing
              {events.length !== 1 ? "s" : ""}
            </span>
          </div>

          <button
            className="all-events-close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="all-events-list">
          {events.map((event, index) => (
            <div
              key={event.id ?? index}
              className={`all-event-item ${event.color}`}
              onClick={() => onEventClick(event)}
              role="button"
              tabIndex={0}
            >
              <div className="all-event-icon">
                {event.color === "green" ? "▣" : "▤"}
              </div>

              <div className="all-event-content">
                <div className="all-event-title">
                  {event.title}
                </div>

                {event.time && (
                  <div className="all-event-time">
                    {event.time}
                  </div>
                )}

                {event.description && (
                  <div className="all-event-description">
                    {event.description}
                  </div>
                )}

                {event.originalEvent?.type && (
                  <div className="all-event-type">
                    {event.originalEvent.type}
                  </div>
                )}
              </div>

              <div className="all-event-arrow">
                ›
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

  return (
    <div className="calendar-container">
      {/* HEADER */}
      <div className="calendar-header">
        <div className="header-left">
          <button className="nav-button" onClick={goToPreviousPeriod}>
            ‹
          </button>

          <h2 className="month-year">
            {view === "month"
              ? `${
                  monthNames[currentDate.getMonth()]
                } ${currentDate.getFullYear()}`
              : formatWeekRange()}
          </h2>

          <button className="nav-button" onClick={goToNextPeriod}>
            ›
          </button>
        </div>

        <div className="header-right">
          <button
            className={`view-button ${view === "week" ? "active" : ""}`}
            onClick={() => setView("week")}
          >
            Week
          </button>
          <button
            className={`view-button ${view === "month" ? "active" : ""}`}
            onClick={() => setView("month")}
          >
            Month
          </button>
          <button className="today-button" onClick={goToToday}>
            Today
          </button>
        </div>
      </div>

      {/* VIEWS */}
      {view === "month" ? renderMonthView() : renderWeekView()}

      {/* POPUP - rendered outside the grid */}
      {activeEvent && (
        <EventPopUp
          ref={popupRef}
          event={activeEvent}
          position={popupPosition}
          onClose={() => setActiveEvent(null)}
          onEventClick={onEventClick}
        />
      )}
      {showAllEvents && (
        <AllEventsDialog
            events={selectedDayEvents}
            onClose={() => setShowAllEvents(false)}
            onEventClick={handleDialogEventClick}
        />
        )}
        
    </div>
  );
};

export default Calendar;

/* -----------------------
   EventPopUp Component
   ----------------------- */

const EventPopUp = React.forwardRef(
  ({ event, position, onClose, onEventClick }, ref) => {
    const handleViewDetails = () => {
      if (onEventClick) {
        onEventClick({
          type: "details",
          action: "details",
          event: event.originalEvent ?? event,
        });
      }
      onClose();
    };

    const handleProceed = () => {
      if (onEventClick) {
        onEventClick({
          type: "action",
          action: "proceed",
          event: event.originalEvent ?? event,
        });
      }
      onClose();
    };

    const handleReschedule = () => {
      if (onEventClick) {
        onEventClick({
          type: "action",
          action: "reschedule",
          event: event.originalEvent ?? event,
        });
      }
      onClose();
    };

    return (
      <div
        ref={ref}
        className="event-popup-overlay"
        style={{
          position: "fixed",
          top: position.y,
          left: position.x,
          zIndex: 9999,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="event-popup-card">
          <div className="popup-event-header">
            <div className="popup-event-case">{event.title}</div>
            {event.description && (
              <div className="popup-event-location">{event.description}</div>
            )}
            {event.time && (
              <div className="popup-event-time">🕐 {event.time}</div>
            )}
          </div>

          <div className="popup-action-buttons">
            <button
              className="popup-action-btn primary-action"
              onClick={handleProceed}
            >
              <span className="action-icon">▶</span>
              <span>Proceed to Hearing</span>
            </button>

            <button
              className="popup-action-btn secondary-action"
              onClick={handleReschedule}
            >
              <span className="action-icon">📅</span>
              <span>Re-Schedule</span>
            </button>

            <button
              className="popup-action-btn secondary-action"
              onClick={handleViewDetails}
            >
              <span className="action-icon">ℹ️</span>
              <span>Case Details</span>
            </button>

            <button
              className="popup-action-btn cancel-action"
              onClick={onClose}
            >
              <span>Cancel</span>
            </button>
          </div>
        </div>
      </div>
    );
  }
);
